import Modal from '@/components/Modal';
import PosButton from '@/components/PosButton';
import { UserInfo } from '@/components/User/UserInfo';
import { codePayTradeCancel, ReceivingMode } from '@/models/payment';
import { PointRules } from '@/models/point';
import { POS_ENDPOINT, POSPaymentResponse } from '@/models/pos';
import { timestampStr } from '@/utils/common';
import { playSound } from '@/utils/sound';
import { MoneyCollectOutlined, PoweroffOutlined, UserOutlined } from '@ant-design/icons';
import { App, Flex } from 'antd';
import React, { useEffect } from 'react';
import { useRequest } from '../../../api/hooks';
import BindUser from '../FastActions/BindUser';
import { usePreOrderStore } from '../store/usePreOrderStore';
import Payment from './paymentArea/payment';
import { useSettlementStore } from './store/useSettlementStore';
import styles from './style.less';
import CardTradeArea from './tradeArea/card';
import CartTradeArea from './tradeArea/cart';
import DirectTradeArea from './tradeArea/direct';
import RechargeTradeArea from './tradeArea/recharge';

export type SettlementProps = {
  onClose: (hasChange?: boolean) => void;
  onReceived: (orderCompleteInfo: POSPaymentResponse) => void;
  pointRules: PointRules;
};

/**
 * POS 结算收银 Modal 主弹窗组件
 *
 * 已使用 Zustand Store (useSettlementStore) 替代了 antd Form 实例。
 * 不再使用 hidden Input 节点传递状态，解耦组件层级关联。
 * 结算模式（mode）、待结清单（preOrder）与绑定客户（userFullInfo）由 usePreOrderStore 提供。
 */
const ReceivingOrderModal: React.FC<SettlementProps> = ({ onClose, onReceived, pointRules }: SettlementProps) => {
  // 结算模式（INVALID 即弹窗关闭；组件仅在 mode !== INVALID 时挂载，挂载即打开）
  const mode = useSettlementStore(s => s.mode);
  const preOrder = usePreOrderStore(s => s.preOrder);
  const userFullInfo = preOrder.userFullInfo;
  const { message } = App.useApp();

  const { send, loading, error } = useRequest<POSPaymentResponse>(
    {
      url: `${POS_ENDPOINT}/trade/pay`,
      method: 'post',
    },
    {
      manual: true,
    }
  );

  // 弹窗挂载 / 用户信息或购物车内容变化（preOrder 引用变化）→ 全量重建结算表单并重算金额
  // （业务规则：上下文变化 → 所填所选重置；init 保留当前 mode，弹窗不因重建关闭）
  useEffect(() => {
    useSettlementStore.getState().init(pointRules);
  }, [preOrder, pointRules]);

  /** 提交交易：从 Zustand Store 导出格式化的 Payload 数据 */
  const onSubmit = () => {
    if (loading) return;

    // 从 Store 导出最终交易 Payload
    const values = useSettlementStore.getState().getSubmitPayload();

    message.destroy();
    if (!values.paymentChannel) {
      message.error('请选择收款方式');
      return;
    }

    if (mode === ReceivingMode.DIRECT && !values.actualPrice) {
      message.error('请输入收款金额');
      return;
    }

    if (values.paymentChannel === 'online' && !values.authCode) {
      message.error('请扫描或输入客户付款码');
      return;
    }

    const loadingMsgKey = `pay-${timestampStr()}`;
    message.loading({
      key: loadingMsgKey,
      content: '收款中，请稍等',
      duration: 0,
    });

    send({
      retry: 5,
      data: values,
    })
      .then(rsp => {
        playSound('money');
        try {
          onReceived(rsp);
        } catch (e) {}
      })
      .catch(v => {
        playSound('error');
        message.error('下单失败: ' + (v.msg || v.message || '网络错误'));
      })
      .finally(() => {
        message.destroy(loadingMsgKey);
      });
  };

  return (
    <>
      {mode !== ReceivingMode.INVALID && (
        <Modal
          className={styles.modal}
          maskClosable={false}
          style={{ top: 20 }}
          styles={{ body: { padding: 0 } }}
          width={1050}
          open
          destroyOnHidden
          closable={false}
          afterClose={() => {
            onClose();
          }}
          onCancel={() => {
            useSettlementStore.getState().close();
          }}
          footer={null}>
          <>
            <div className={styles.modalTitle}>
              <div className={styles.modalTitleMain}>
                <MoneyCollectOutlined /> 结算 & 收银
              </div>
              <div className={styles.modalTitleUser}>
                <BindUser
                  userFullInfo={userFullInfo}
                  renderButton={userFullInfo => {
                    return (
                      <PosButton type={'primary'} size={'middle'} shape={'round'}>
                        {userFullInfo ? (
                          <UserInfo user={userFullInfo.user}>
                            {({ Field }) => {
                              return (
                                <Flex align="center" gap={5}>
                                  <Field type="avatar" avatarProps={{ size: 24 }} />
                                  <Field type="name" />
                                </Flex>
                              );
                            }}
                          </UserInfo>
                        ) : (
                          <>
                            <UserOutlined /> 绑定该客户
                          </>
                        )}
                      </PosButton>
                    );
                  }}
                />
              </div>
              <PosButton
                size={'middle'}
                loading={loading}
                danger
                type="primary"
                icon={<PoweroffOutlined />}
                onClick={() => {
                  useSettlementStore.getState().close();
                }}>
                取消
              </PosButton>
            </div>

            <div className={styles.container}>
              <div className={styles.tradeArea}>
                {mode === ReceivingMode.RECHARGE && <RechargeTradeArea userFullInfo={userFullInfo} />}

                {mode === ReceivingMode.DIRECT && <DirectTradeArea />}

                {mode === ReceivingMode.CARD && <CardTradeArea cards={preOrder?.cards || []} />}

                {mode === ReceivingMode.CART && (
                  <CartTradeArea
                    preOrder={preOrder}
                    pointRules={pointRules}
                    balancePayAvailable={mode === ReceivingMode.CART}
                  />
                )}
              </div>

              <div className={styles.paymentArea}>
                <Payment
                  user={userFullInfo}
                  requestLoading={loading}
                  requestError={error}
                  balancePayAvailable={mode === ReceivingMode.CART}
                  onRequestCodePayTradeCancel={async payInfo => {
                    await codePayTradeCancel(payInfo);
                    message.success('撤销成功，请等待交易状态更新');
                  }}
                  onSubmit={onSubmit}
                />
              </div>
            </div>
          </>
        </Modal>
      )}
    </>
  );
};

export default ReceivingOrderModal;
